import { useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Hash, RefreshCw, Save } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

/**
 * Secret key + IV behind every dealer's md hash (see backend/md_hash.py).
 * System Administrator only, enforced by the backend on every call; the card
 * renders nothing for anyone else. Both fields are write-only — the backend
 * never returns them — and saving a new value regenerates every md hash.
 * "Regenerate all" recomputes from the stored values without changing them.
 */
export function MdHashSettingsCard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const allowed = hasGlobalPermission(user, PERM.SYSTEM_MD_HASH_MANAGE);

  const [info, setInfo] = useState(null);
  const [secret, setSecret] = useState("");
  const [iv, setIv] = useState("");
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    client.get("/admin/md-hash").then(({ data }) => setInfo(data)).catch(() => {});
  }, [allowed]);

  if (!allowed) return null;

  const run = async (request, setBusy) => {
    setBusy(true);
    try {
      const { data } = await request();
      setInfo(data);
      setSecret("");
      setIv("");
      toast.success(t("settings.mdHashRegenerated", data.generated));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || t("common.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    run(() => client.put("/admin/md-hash", { secret: secret.trim() || null, iv: iv.trim() || null }), setSaving);
  const regenerateAll = () => {
    setConfirmOpen(false);
    return run(() => client.post("/admin/md-hash/regenerate"), setRegenerating);
  };

  const ivInvalid = iv.trim() !== "" && iv.trim().length !== 16;
  const busy = saving || regenerating;

  return (
    <Card className="mb-6 p-6" data-testid="settings-md-hash-card">
      <div className="mb-1 flex items-center gap-2">
        <Hash className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">{t("settings.mdHash")}</h2>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">{t("settings.mdHashSub")}</p>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <Label className="data-label mb-1 block">{t("settings.mdHashSecret")}</Label>
          <div className="flex gap-2">
            <Input
              data-testid="settings-md-hash-secret"
              type="password"
              autoComplete="new-password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={info?.secret_is_set ? t("settings.mdHashSetPlaceholder") : t("settings.mdHashNotSet")}
              disabled={busy}
              className="font-mono text-xs"
            />
            <Button
              data-testid="settings-md-hash-regenerate"
              variant="outline"
              onClick={() => setConfirmOpen(true)}
              disabled={busy || !info?.secret_is_set || !info?.iv_is_set}
              className="shrink-0 gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${regenerating ? "animate-spin" : ""}`} />
              {t("settings.mdHashRegenerateAll")}
            </Button>
          </div>
        </div>
        <div>
          <Label className="data-label mb-1 block">{t("settings.mdHashIv")}</Label>
          <Input
            data-testid="settings-md-hash-iv"
            type="password"
            autoComplete="new-password"
            value={iv}
            maxLength={16}
            onChange={(e) => setIv(e.target.value)}
            placeholder={info?.iv_is_set ? t("settings.mdHashSetPlaceholder") : t("settings.mdHashNotSet")}
            disabled={busy}
            aria-invalid={ivInvalid}
            className="font-mono text-xs"
          />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground" data-testid="settings-md-hash-status">
          {info ? t("settings.mdHashStatus", info.hashes_stored, info.dealers_total) : ""}
        </p>
        <Button
          data-testid="settings-md-hash-save"
          onClick={save}
          disabled={busy || ivInvalid || (!secret.trim() && !iv.trim())}
          className="gap-1.5"
        >
          <Save className="h-3.5 w-3.5" />
          {t("common.save")}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="settings-md-hash-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("settings.mdHashRegenerateTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("settings.mdHashRegenerateBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="settings-md-hash-confirm-proceed" onClick={regenerateAll}>
              {t("settings.mdHashRegenerateAll")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
